import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { Button } from '../../ui/Button';
import { opportunityWatchService } from '../../../services/opportunityWatchService';
import { WatchFindings } from './WatchFindings';

const STATUS = {
  watching: ['Watching', 'bg-blue-100 text-blue-700'],
  new_info: ['New information found', 'bg-green-100 text-green-700'],
  needs_review: ['Needs review', 'bg-amber-100 text-amber-700'],
  paused: ['Paused', 'bg-slate-100 text-slate-600'],
  completed: ['Completed', 'bg-slate-100 text-slate-500']
};
const FREQ = { weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly' };
const field = 'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-meetoi-navy';
const when = (v) => (v ? new Date(v).toLocaleString() : 'Not yet');

export const WatchCard = ({ userId, watch, contacts, onContactAdded, onFlash, onChanged, startOpen = false }) => {
  const [showFindings, setShowFindings] = useState(startOpen);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [label, color] = STATUS[watch.status] || STATUS.watching;
  const ended = watch.status === 'completed';

  const run = async (fn, okText) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      if (okText) onFlash('success', okText);
      onChanged();
    } catch (e) {
      onFlash('error', e.message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setForm({
      title: watch.title,
      instructions: watch.instructions,
      trigger: watch.trigger_description,
      frequency: watch.frequency,
      stopDay: watch.stop_after || '',
      consent: watch.unattended_consent
    });
    setEditing(true);
  };

  const saveEdit = () =>
    run(async () => {
      await opportunityWatchService.update(watch.id, userId, form);
      setEditing(false);
    }, 'Watch updated.');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="rounded-xl border border-violet-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-violet-700">
          <SafeIcon icon={FiIcons.FiEye} className="mr-1" /> Opportunity Watch
        </span>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${color}`}>{label}</span>
      </div>
      <h3 className="mt-2 text-base font-bold text-slate-900">{watch.title}</h3>
      <p className="mt-1 text-sm text-slate-700">{watch.instructions}</p>
      {watch.trigger_description && <p className="mt-1 text-xs text-slate-500">Alert when: {watch.trigger_description}</p>}
      <p className="mt-2 text-xs text-slate-500">
        {FREQ[watch.frequency]} checks
        {watch.contacts?.full_name ? ` · About ${watch.contacts.full_name}` : ''}
        {watch.moment_id ? ' · Linked to a Moment' : ''}
        {watch.stop_after ? ` · Stops ${watch.stop_after}` : ''}
      </p>
      <p className="text-xs text-slate-500">
        Last checked: {when(watch.last_checked_at)}
        {!ended && watch.status !== 'paused' && watch.next_check_at ? ` · Next check: ${when(watch.next_check_at)}` : ''}
      </p>
      {watch.last_result && <p className="mt-1 text-xs text-slate-600">{watch.last_result}</p>}
      {watch.last_error && <p className="mt-1 text-xs font-medium text-amber-700">{watch.last_error}</p>}

      {editing && form && (
        <div className="mt-3 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <label className="block text-xs font-medium text-slate-600">Subject
            <input className={`${field} mt-1`} value={form.title} maxLength={200} onChange={(e) => set('title', e.target.value)} />
          </label>
          <label className="block text-xs font-medium text-slate-600">What to look for
            <textarea className={`${field} mt-1`} rows={2} value={form.instructions} maxLength={1000} onChange={(e) => set('instructions', e.target.value)} />
          </label>
          <label className="block text-xs font-medium text-slate-600">Alert me when
            <input className={`${field} mt-1`} value={form.trigger} maxLength={500} onChange={(e) => set('trigger', e.target.value)} />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block text-xs font-medium text-slate-600">How often
              <select className={`${field} mt-1`} value={form.frequency} onChange={(e) => set('frequency', e.target.value)}>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </label>
            <label className="block text-xs font-medium text-slate-600">Stop after (optional)
              <input type="date" className={`${field} mt-1`} value={form.stopDay} onChange={(e) => set('stopDay', e.target.value)} />
            </label>
          </div>
          <label className="flex items-start gap-2 text-xs text-slate-600">
            <input type="checkbox" className="mt-0.5" checked={form.consent} onChange={(e) => set('consent', e.target.checked)} />
            If I use my own OpenAI key, allow scheduled checks to use it. Each check is billed to my OpenAI account.
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveEdit} isLoading={busy} disabled={!form.title.trim() || !form.instructions.trim()}>Save</Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={() => setShowFindings((v) => !v)}>
          {showFindings ? 'Hide history' : 'Findings & history'}
        </Button>
        {!ended && <Button size="sm" variant="secondary" onClick={startEdit}>Edit</Button>}
        {(watch.status === 'watching' || watch.status === 'new_info') && (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => run(() => opportunityWatchService.setStatus(watch.id, userId, 'paused'), 'Watch paused.')}>Pause</Button>
        )}
        {(watch.status === 'paused' || watch.status === 'needs_review') && (
          <Button size="sm" disabled={busy} onClick={() => run(() => opportunityWatchService.setStatus(watch.id, userId, 'watching'), 'Watch resumed. It will be checked shortly.')}>
            {watch.status === 'paused' ? 'Resume' : 'Retry'}
          </Button>
        )}
        {watch.status === 'completed' && (
          <Button size="sm" disabled={busy} onClick={() => run(() => opportunityWatchService.setStatus(watch.id, userId, 'watching'), 'Watch restarted.')}>Restart</Button>
        )}
        {!ended && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => opportunityWatchService.setStatus(watch.id, userId, 'completed'), 'Watch stopped.')}>Stop</Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="text-slate-400 hover:text-red-500"
          disabled={busy}
          onClick={() => {
            if (window.confirm('Delete this watch and its history? This cannot be undone.')) {
              run(() => opportunityWatchService.remove(watch.id, userId), 'Watch deleted.');
            }
          }}
        >
          Delete
        </Button>
      </div>

      {showFindings && (
        <WatchFindings
          userId={userId}
          watch={watch}
          contacts={contacts}
          onContactAdded={onContactAdded}
          onFlash={onFlash}
          onChanged={onChanged}
        />
      )}
    </div>
  );
};